import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingDown, X } from 'lucide-react';
import { useHistoricoPrecos } from '@/features/compras/hooks/useHistoricoPrecos';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { ProdutoNome } from '@/components/ProdutoNome';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

/** dt_referencia é `date` ("yyyy-MM-dd"): formatar sem passar por Date (fuso volta 1 dia). */
const fmtDia = (s: string) => {
  const [a, m, d] = s.slice(0, 10).split('-');
  return d ? `${d}/${m}/${a}` : s;
};

// USA-COM-14: período + paginação no servidor (antes: 100 registros fixos).
const HistoricoPrecos = () => {
  const navigate = useNavigate();
  const [produtoId,    setProdutoId]    = useState<number | undefined>();
  const [fornecedorId, setFornecedorId] = useState<number | undefined>();
  const [periodo,      setPeriodo]      = useState({ inicio: '', fim: '' });
  const [buscar,       setBuscar]       = useState(false);

  const pag = usePaginacaoServidor({ resetarQuando: [produtoId, fornecedorId, periodo] });

  const params = {
    produtoId,
    fornecedorId,
    dtInicio: periodo.inicio || undefined,
    dtFim:    periodo.fim || undefined,
    pagina:        pag.pagina,
    tamanhoPagina: pag.tamanhoPagina,
  };

  const { data, isLoading } = useHistoricoPrecos(buscar ? params : {});
  const itens = data?.itens ?? [];

  const handleBuscar = () => setBuscar(true);

  const menor = itens.length > 0 ? Math.min(...itens.map(i => i.custoUnitario)) : null;
  const maior = itens.length > 0 ? Math.max(...itens.map(i => i.custoUnitario)) : null;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Histórico de Preços</h1>
        <p className="text-muted-foreground mt-1">Evolução do custo de compra por produto e fornecedor</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader><CardTitle className="text-base">Filtros</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2 w-80">
              <Label>Produto</Label>
              <div className="flex gap-1">
                <ProdutoSearchCombobox value={produtoId} onChange={setProdutoId}
                  placeholder="Todos os produtos" />
                {produtoId && (
                  <Button variant="ghost" size="icon" title="Limpar produto"
                    onClick={() => setProdutoId(undefined)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2 w-80">
              <Label>Fornecedor</Label>
              <FornecedorCombobox value={fornecedorId ?? null}
                onChange={id => setFornecedorId(id ?? undefined)}
                placeholder="Todos os fornecedores" />
            </div>
            <FiltroPeriodo rotulo="Data" inicio={periodo.inicio} fim={periodo.fim}
              onChange={({ inicio, fim }) => setPeriodo({ inicio, fim })} />
            <Button className="gap-2" onClick={handleBuscar}>
              <Search className="h-4 w-4" />Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {itens.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Menor Custo (página)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-green-600">
                {menor?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Maior Custo (página)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-red-600">
                {maior?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />Registros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{data?.total ?? itens.length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead>Fornecedor</TableHead>
                <TableHead className="text-right">Custo Unit.</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead>NF</TableHead>
                <TableHead>Recebimento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!buscar ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Use os filtros acima para buscar o histórico de preços
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              ) : itens.map(h => (
                <TableRow key={h.id}>
                  <TableCell>{fmtDia(h.dtReferencia)}</TableCell>
                  <TableCell><ProdutoNome id={h.produtoId} /></TableCell>
                  <TableCell>{h.nomeFornecedor ?? '(fornecedor não encontrado)'}</TableCell>
                  <TableCell className="text-right font-mono">
                    {h.custoUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell className="text-right font-mono">{h.quantidade}</TableCell>
                  <TableCell>{h.numeroNf ?? '—'}</TableCell>
                  <TableCell>
                    {h.recebimentoId ? (
                      <Button variant="link" className="h-auto p-0"
                        onClick={() => navigate(`/compras/recebimentos/${h.recebimentoId}/ajuste-preco`)}>
                        Abrir
                      </Button>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {buscar && <PaginacaoRodape {...pag.rodape(data)} />}
    </div>
  );
};

export default HistoricoPrecos;
